import AdditionalConsolidationAmountField from '../../components/AdditionalConsolidationAmountField/AdditionalConsolidationAmountField';
import Description from '../../components/Description/Description';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import RadioField from '../../components/RadioField/RadioField';
import ShowDetailsModal from '../../components/ShowDetailsModal/ShowDetailsModal';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useFieldToggle from '../../hooks/useFieldToggle';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldStateByName, selectFieldValue } from '../../store/applicationform/applicationFormSelectors';
import { WizardPageAction } from '../../store/wizard/wizard';

const PurposeOfLoanPage = () => {
    useComponentTracker('PurposeOfLoanPage');
    const dispatch = useAppDispatch();
    const translate = useTranslation();
    const defaultPagesAction = useDefaultPagesAction();
    const { totalSections, totalAboutTheLoanPages } = usePagesConfig();
    const { currentPage } = usePagesContext();
    useSetBannerType('bannerOne');
    const loanReason = useAppSelector(selectFieldValue)('loanReason');
    const consolidationAmountFieldState = useAppSelector(selectFieldStateByName)('additionalConsolidationAmount');
    useFieldToggle(loanReason === 'CONSOLIDATE_LOANS', ['additionalConsolidationAmount']);

    const validationErrors = useValidation(['loanReason']);

    const onAction = async (action: WizardPageAction) => {
        if (validationErrors.length) {
            scrollToFieldWithError(validationErrors);
            validationErrors.forEach((validationError) => {
                dispatch(addOrUpdateFormField(validationError));
            });
            return false;
        }

        if (loanReason === 'CONSOLIDATE_LOANS' && consolidationAmountFieldState?.hasError) {
            scrollToFieldWithError([consolidationAmountFieldState]);
            return false;
        }

        return defaultPagesAction(action);
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={defaultPagesAction}
                currentPage={currentPage}
                currentSection={1}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutTheLoanPages}
                currentPageInSection={0}
                sectionTitle={translate('field.section.about-the-loan')}
            />
            <Title text={translate('page.purpose-of-loan.title')} />
            <Description text={translate('page.purpose-of-loan.description')} />
            <ShowDetailsModal
                label={translate('dialog.not-sure-what-to-choose.label')}
                dialogTitle={translate('dialog.not-sure-what-to-choose.label')}
                dialogContent={translate('page.purpose-of-loan.content')}
            />
            <RadioField
                name="loanReason"
                options={[
                    { id: 'CONSOLIDATE_LOANS', label: translate('field.purpose-of-loan.option.consolidate.label') },
                    {
                        id: 'DENTAL_OR_OTHER_CARE',
                        label: translate('field.purpose-of-loan.option.dental-or-other-care.label'),
                    },
                    { id: 'INTERIOR', label: translate('field.purpose-of-loan.option.interior.label') },
                    { id: 'MOTOR', label: translate('field.purpose-of-loan.option.motor.label') },
                    { id: 'RENOVATION', label: translate('field.purpose-of-loan.option.renovation.label') },
                    { id: 'TRAVEL', label: translate('field.purpose-of-loan.option.travel.label') },
                    { id: 'WEDDING', label: translate('field.purpose-of-loan.option.wedding.label') },
                    { id: 'OTHER', label: translate('field.purpose-of-loan.option.other.label') },
                ]}
            />
            {loanReason === 'CONSOLIDATE_LOANS' && <AdditionalConsolidationAmountField />}
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={1}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutTheLoanPages}
                currentPageInSection={0}
                sectionTitle={translate('field.section.about-the-loan')}
            />
        </>
    );
};

export default PurposeOfLoanPage;
